import {apiFetch} from "../client";
import type {BalanceMovementsParams, BalanceMovementsResponse} from "../client";
import type {MerchantResponse} from "./merchants";

// Денежная поверхность мерчанта: леджер, разрез по командам, ручные ДДС-операции
// и остаток, с которым сравнивает предоплатный claim-гейт. Все суммы приходят с
// бэкенда десятичными строками и такими же остаются — во float их здесь не
// переводят ни на входе, ни на выходе.

/** POST /v1/merchant/{id}/balance/reset — admin-only reset to zero. */
export function resetMerchantBalance(token: string | null, id: string): Promise<MerchantResponse> {
    return apiFetch<MerchantResponse>(`/merchant/${encodeURIComponent(id)}/balance/reset`, token, {
        method: "POST",
    });
}

/** GET /v1/merchant/{id}/balance/movements - admin-only balance ledger read model. */
export function getMerchantBalanceMovements(
    token: string | null,
    id: string,
    query: BalanceMovementsParams = {},
): Promise<BalanceMovementsResponse> {
    const params = new URLSearchParams();
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.offset != null) params.set("offset", String(query.offset));
    const suffix = params.toString();
    return apiFetch<BalanceMovementsResponse>(
        `/merchant/${encodeURIComponent(id)}/balance/movements${suffix ? `?${suffix}` : ""}`,
        token,
    );
}

/**
 * Response of GET /v1/merchant/{id}/balance/by-team (backend
 * merchant.BalanceByTeamResponse). `items[].amount` keeps the merchant's own
 * sign (negative = merchant debt accumulated through that team) — unlike the
 * trader-facing by-merchant view, this is NOT mirrored.
 */
export interface BalanceByTeamItem {
    teamId: string;
    teamName: string;
    amount: string;
}

export interface BalanceByTeamResponse {
    merchantBalance: string;
    currency: string;
    items: BalanceByTeamItem[];
    unattributed: string | null;
}

/** GET /v1/merchant/{id}/balance/by-team - admin-only per-team breakdown of the merchant's ledger balance. */
export function getMerchantBalanceByTeam(token: string | null, id: string): Promise<BalanceByTeamResponse> {
    return apiFetch<BalanceByTeamResponse>(`/merchant/${encodeURIComponent(id)}/balance/by-team`, token);
}

export type MerchantBalanceOperationType = "topup" | "withdrawal";

/**
 * Body of POST /v1/merchant/{id}/balance/operations.
 *
 * `amount` is unsigned — the direction is `type`. `operationKey` is generated
 * once per operation (not per attempt) so a retry after a network failure
 * replays the original instead of moving the balance twice.
 */
export interface MerchantBalanceOperationRequest {
    type: MerchantBalanceOperationType;
    amount: string;
    operationKey: string;
}

export interface MerchantBalanceOperationResponse {
    operation: {
        id: string;
        type: MerchantBalanceOperationType;
        amount: string;
        occurredAt: string;
    };
    /** Merchant balance right after the operation, as a USDT string. */
    balance: string;
    currency: string;
}

/** POST /v1/merchant/{id}/balance/operations — admin-entered top-up or withdrawal. */
export function applyMerchantBalanceOperation(
    token: string | null,
    id: string,
    body: MerchantBalanceOperationRequest,
): Promise<MerchantBalanceOperationResponse> {
    return apiFetch<MerchantBalanceOperationResponse>(
        `/merchant/${encodeURIComponent(id)}/balance/operations`,
        token,
        { method: "POST", body: JSON.stringify(body) },
    );
}

/**
 * Response of GET /v1/merchant/{id}/balance/availability.
 *
 * All amounts are USDT strings and stay strings here: the backend already
 * computed `available` with the same code the claim gate runs, and re-deriving
 * it through JS numbers would both lose precision and risk showing the operator
 * a different number than the gate compares.
 */
export interface MerchantBalanceAvailabilityResponse {
    overdraftEnabled: boolean;
    balance: string;
    overdraftAllowance: string;
    /** Estimated weight of orders traders already hold; a lower bound when hasUnknownInFlight. */
    knownInFlight: string;
    hasUnknownInFlight: boolean;
    available: string;
    currency: string;
}

/** GET /v1/merchant/{id}/balance/availability — headroom the prepaid claim gate compares. */
export function getMerchantBalanceAvailability(
    token: string | null,
    id: string,
): Promise<MerchantBalanceAvailabilityResponse> {
    return apiFetch<MerchantBalanceAvailabilityResponse>(
        `/merchant/${encodeURIComponent(id)}/balance/availability`,
        token,
    );
}

/**
 * Одно открытое расхождение сверок долгового кабинета.
 *
 * `operationType`, `amount` и `operationKey` — не описание расхождения, а
 * готовая проводка, которая его закрывает: форма подставляет их как есть.
 * Пусто у вида, который проводкой не закрывается (площадка не заплатила вовсе).
 */
export interface DebtDiscrepancyItem {
    kind: "withdrawal_uncovered" | "deposit_excess" | "order_mismatch" | "order_unpaid";
    externalId?: string;
    depositId?: string;
    occurredAt?: string;
    amount: string;
    operationType?: "topup" | "withdrawal";
    operationKey?: string;
    /** По расхождению уже горит алерт. Ложь не значит «не требует внимания». */
    alerting: boolean;
}

export interface DebtDiscrepanciesResponse {
    items: DebtDiscrepancyItem[];
    currency: string;
}

/** GET /v1/merchant/{id}/debt-discrepancies — открытые расхождения долгового кабинета. */
export function getDebtDiscrepancies(
    token: string | null,
    id: string,
): Promise<DebtDiscrepanciesResponse> {
    return apiFetch<DebtDiscrepanciesResponse>(
        `/merchant/${encodeURIComponent(id)}/debt-discrepancies`,
        token,
    );
}
